import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  listStaffAlerts,
  markStaffAlertRead,
} from '../../services/staffAlertsApi';
import {
  getStaffAlertDedupeKey,
} from '../../utils/staffAlerts.mjs';

const POLL_INTERVAL_MS = 30000;
const SESSION_SHOWN_KEY = 've_staff_alerts_shown_keys';
const MAX_QUEUE_SIZE = 10;

function getStoredShownKeys() {
  if (typeof window === 'undefined') return new Set();

  try {
    const stored = window.sessionStorage.getItem(SESSION_SHOWN_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (_) {
    return new Set();
  }
}

function storeShownKeys(keys) {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(SESSION_SHOWN_KEY, JSON.stringify(Array.from(keys).slice(-200)));
  } catch (_) {
    // Storage can be unavailable in hardened browser modes. The in-memory set
    // still prevents duplicate popups until this provider unmounts.
  }
}

function hasStoredToken() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.localStorage.getItem('token') || window.localStorage.getItem('access_token'));
}

function normalizeAlerts(data) {
  return Array.isArray(data?.alerts) ? data.alerts : [];
}

export default function StaffAlertsProvider({ children }) {
  const { user, accessToken, loading } = useAuth();
  const shownKeysRef = useRef(getStoredShownKeys());
  const queuedKeysRef = useRef(new Set());
  const [queue, setQueue] = useState([]);
  const [activeAlert, setActiveAlert] = useState(null);

  const isAuthenticated = useMemo(() => {
    if (loading) return false;
    return Boolean(user || accessToken || hasStoredToken());
  }, [accessToken, loading, user]);

  const rememberShownKey = useCallback((key) => {
    shownKeysRef.current.add(key);
    storeShownKeys(shownKeysRef.current);
  }, []);

  const loadUnreadAlerts = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const { data } = await listStaffAlerts({ unread: 1, limit: 50 });
      const unreadAlerts = normalizeAlerts(data).filter((alert) => !alert.read_at && !alert.dismissed_at);
      const nextAlerts = [];

      unreadAlerts.forEach((alert) => {
        const key = getStaffAlertDedupeKey(alert);
        if (shownKeysRef.current.has(key) || queuedKeysRef.current.has(key)) return;

        rememberShownKey(key);
        queuedKeysRef.current.add(key);
        nextAlerts.push(alert);
      });

      if (nextAlerts.length > 0) {
        setQueue((current) => [...current, ...nextAlerts].slice(0, MAX_QUEUE_SIZE));
      }
    } catch (_) {
      // The staff-alert backend may be absent during migration. No toast/log.
    }
  }, [isAuthenticated, rememberShownKey]);

  useEffect(() => {
    if (!isAuthenticated) {
      setQueue([]);
      setActiveAlert(null);
      queuedKeysRef.current.clear();
      return undefined;
    }

    loadUnreadAlerts();
    const intervalId = window.setInterval(loadUnreadAlerts, POLL_INTERVAL_MS);
    const handleFocus = () => loadUnreadAlerts();
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, loadUnreadAlerts]);

  useEffect(() => {
    if (activeAlert || queue.length === 0) return;

    const [nextAlert, ...remainingAlerts] = queue;
    setActiveAlert(nextAlert);
    setQueue(remainingAlerts);
  }, [activeAlert, queue]);

  const clearActiveAlert = useCallback((alert) => {
    if (alert) {
      queuedKeysRef.current.delete(getStaffAlertDedupeKey(alert));
    }
    setActiveAlert((current) => (current?.id === alert?.id ? null : current));
  }, []);

  useEffect(() => {
    if (!activeAlert) return undefined;
    let isCurrent = true;

    const markActiveAlertRead = async () => {
      try {
        await markStaffAlertRead(activeAlert.id);
      } catch (_) {
        // Silent by design; OS push handles alert visibility.
      } finally {
        if (isCurrent) {
          clearActiveAlert(activeAlert);
        }
      }
    };

    markActiveAlertRead();

    return () => {
      isCurrent = false;
    };
  }, [activeAlert, clearActiveAlert]);

  if (!isAuthenticated) {
    return children;
  }

  return <>{children}</>;
}
